import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import './SelectingInput.css';
import React, { useEffect, useId, useState } from 'react';
import { getKeyHandler } from '../structure/FlowForm';
import Spinner from './Spinner';
import { globalFocus } from '../helpers/dom';
import { chevronDownIcon } from '../icons';
import Icon from './Icon';
import { debounce3 } from '../helpers/debounce';
import { OrRuler } from '../structure/OrRuler';
import { Stack } from '../structure/Stack';
const LIST_LENGTH = 10;
const SelectingInput = (({ className, size = 'medium', value, placeholder = '', noSelectionText = '', maxLength = 100, sidewaysBehaviour = 'off', onSearch, onSelect, prefix = '', focusId }) => {
    var _a, _b;
    const containerRef = React.useRef(null);
    const debounceOnSelect = onSearch && debounce3(1, onSearch, 50);
    const [state, setState] = React.useState({ state: 'close', displaySearch: '', listedSearch: '***', list: [], hasMore: false });
    const selectionRef = React.useRef(null);
    const isLoading = state.state == 'open' && state.displaySearch != state.listedSearch;
    const id = focusId !== null && focusId !== void 0 ? focusId : useId();
    function close(item) {
        7;
        setState(s => { var _a, _b; return ({ ...s, state: 'close', displaySearch: (_b = (_a = item === null || item === void 0 ? void 0 : item.text) !== null && _a !== void 0 ? _a : value === null || value === void 0 ? void 0 : value.text) !== null && _b !== void 0 ? _b : '' }); });
        if (item && item.id != (value === null || value === void 0 ? void 0 : value.id))
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(item);
    }
    const [prevState, setPrevState] = useState('close');
    useEffect(() => {
        if (prevState == 'open' && state.state == 'close' && selectionRef.current) {
            globalFocus(selectionRef.current);
        }
        setPrevState(state.state);
    }, [state.state, selectionRef.current]);
    const handleKeyDown = getKeyHandler({
        sidewaysBehaviour: sidewaysBehaviour,
        enterBehaviour: 'action',
        overrideHandlers: {
            'Space': () => { request(''); return true; },
        },
        onAction() {
            request('');
        },
        defaultHandler: (key) => {
            if (/^[a-zA-Z0-9]$/.test(key)) {
                request(key);
                return true;
            }
            return false;
        },
    });
    const handleInputKeyDown = getKeyHandler({
        sidewaysBehaviour: sidewaysBehaviour,
        enterBehaviour: 'action',
        overrideHandlers: {
            'Escape': () => { close(); return true; },
        },
        onAction() {
            if (state.list.length == 1) {
                close(state.list[0]);
            }
        }
    });
    const handleItemKeyDown = getKeyHandler({
        enterBehaviour: 'action',
        spaceBehaviour: 'action',
        onAction: (target) => {
            close(state.list.find((item) => { var _a; return item.id == ((_a = target === null || target === void 0 ? void 0 : target.dataset) === null || _a === void 0 ? void 0 : _a.id); }));
        },
        upDownBehaviour: (origin) => origin.dataset.last == 'true' ? 'up' : 'both',
        overrideHandlers: { 'Escape': () => { close(); return true; } },
    });
    function request(search) {
        setState(s => ({ ...s, state: 'open', displaySearch: search }));
        debounceOnSelect === null || debounceOnSelect === void 0 ? void 0 : debounceOnSelect(search, LIST_LENGTH, (listedSearch, list, hasMore) => {
            // Before overwriting, check whether we don't already have the right value and we got a late result
            setState(s => (((s.displaySearch == s.listedSearch && s.displaySearch != listedSearch) || s.state == 'close') ? s : { ...s, list, listedSearch, hasMore }));
        });
    }
    useEffect(() => {
        function handleClickOutside(event) {
            const container = containerRef.current;
            if (container && !container.contains(event.target) && state.state != 'close')
                close();
        }
        if (state.state != 'close')
            document.addEventListener('mousedown', handleClickOutside);
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [state, containerRef.current]);
    const handleChange = (e) => {
        request(e.target.value);
    };
    return _jsxs("div", { ref: containerRef, className: `selectingInput ${state.state} ${size != 'medium' ? ` ${size}` : ''} ${className !== null && className !== void 0 ? className : ''}`, onClick: () => {
            var _a;
            if (state.state == 'close')
                request('');
            (_a = document.getElementById(id + '-input')) === null || _a === void 0 ? void 0 : _a.focus();
        }, 
        // We can keep the onBlur handler as a backup
        onBlur: (e) => {
            var _a;
            // Only process blur if the related target is not within our component
            if (!e.relatedTarget || ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)))
                return;
            close();
        }, children: [_jsxs("div", { className: 'top', children: [(value === null || value === void 0 ? void 0 : value.avatarUrl) && _jsx("img", { src: value.avatarUrl, alt: value.text }), state.state == 'open' ? _jsxs(_Fragment, { children: [_jsx("input", { id: id + '-input', className: `${value ? 'selected' : 'unselected'} flowFocussable`, value: state.displaySearch, onKeyDown: handleInputKeyDown, onClick: () => { if (state.state != 'close')
                                    return; request(''); }, autoFocus: true, placeholder: placeholder, maxLength: maxLength, onChange: handleChange, enterKeyHint: 'search', autoComplete: 'off' }), _jsx(Stack, { children: _jsx(Spinner, { className: isLoading ? 'showDelayed' : '' }) })] })
                        : _jsxs(_Fragment, { children: [_jsx("div", { ref: selectionRef, className: `selection flowFocussable${sidewaysBehaviour == 'off' ? '' : ' sidewayFocus'}`, onKeyDown: handleKeyDown, id: id, tabIndex: 0, children: (_a = value === null || value === void 0 ? void 0 : value.selectionText) !== null && _a !== void 0 ? _a : ((value === null || value === void 0 ? void 0 : value.text) ? (prefix + (value === null || value === void 0 ? void 0 : value.text)) : noSelectionText) }), _jsx(Icon, { icon: chevronDownIcon, size: 'medium', className: state.state == 'close' ? 'show' : '' })] })] }, 'top'), state.state == 'open' && _jsxs("div", { className: `selectingInputList`, children: [((_b = state.list[0]) === null || _b === void 0 ? void 0 : _b.group) && _jsx(OrRuler, { text: state.list[0].group, position: 'mid' }, 'group0'), state.list.flatMap((item, i) => {
                        var _a;
                        const selected = (value === null || value === void 0 ? void 0 : value.id) == item.id;
                        const showGroup = (i + 2) <= state.list.length && item.group != state.list[i + 1].group && state.list[i + 1].group;
                        return [
                            _jsxs("div", { className: `item flowFocussable ${selected ? 'selected' : ''}${showGroup ? ' groupEnd' : ''}`, tabIndex: 0, "data-id": item.id, "data-last": i == state.list.length - 1 ? 'true' : 'false', onKeyDown: handleItemKeyDown, onClick: () => { close(item); }, children: [item.avatarUrl && _jsx("img", { src: item.avatarUrl, alt: item.text }), _jsx("span", { children: item.text })] }, item.id),
                            ...(showGroup ? [_jsx(OrRuler, { text: state.list[i + 1].group, position: 'mid' }, i + ((_a = state.list[i + 1].group) !== null && _a !== void 0 ? _a : 'g'))] : [])
                        ];
                    }), isLoading && state.list.length == 0 && _jsx("div", { className: `loading`, children: "Searching..." }, 'loading'), state.list.length == 0 && !isLoading && _jsx("div", { className: `noResults`, children: "No results found." }, 'noResults'), state.list.length > 0 && state.hasMore && _jsx("div", { className: `hasMore`, children: "Refine search for more...." }, 'hasMore')] }, 'open')] });
});
export function selectionInputCacheWrapper(onSearch, supportSubTerms = true) {
    const CACHE_SIZE = 32;
    const REQUEST_FACTOR = 2;
    let cache = {};
    let lastMaxItem = 0;
    let lastRequest = {
        search: '****',
        list: [],
        hasMore: false,
    };
    return (search, maxItem, setter) => {
        if (maxItem != lastMaxItem) {
            cache = {};
        }
        lastMaxItem = maxItem;
        if (cache[search]) {
            const r = cache[search];
            if (Object.keys(cache).length > CACHE_SIZE)
                cache = {};
            lastRequest = {
                search,
                list: r.list,
                hasMore: r.hasMore,
            };
            setter(search, r.list.slice(0, maxItem), r.hasMore);
            return;
        }
        // same search: same result
        if (lastRequest.search == search) {
            setter(search, lastRequest.list, lastRequest.hasMore);
            return;
        }
        // added characters to last search: try to drill down without request
        if (search.trim().startsWith(lastRequest.search.trim()) && lastRequest.list.length < maxItem * REQUEST_FACTOR) {
            if (supportSubTerms) {
                const lc = search.toLowerCase().trim().split(/\s+/);
                const filtered = lastRequest.list.filter((item) => lc.every((l) => item.searchTextLc.includes(l)));
                setter(search, filtered.slice(0, maxItem), lastRequest.hasMore || filtered.length > maxItem);
            }
            else {
                const lc = search.toLowerCase().trim();
                const filtered = lastRequest.list.filter((item) => item.searchTextLc.includes(lc));
                setter(search, filtered.slice(0, maxItem), lastRequest.hasMore || filtered.length > maxItem);
            }
            // don't update lastRequest. We would just lose info doing that.
            return;
        }
        // ok, no shortcut found. Do a new request
        if (Object.keys(cache).length > CACHE_SIZE)
            cache = {};
        onSearch(search, maxItem * REQUEST_FACTOR, (search, list, hasMore) => {
            lastRequest = {
                search,
                list,
                hasMore,
            };
            cache[search] = lastRequest;
            setter(search, list.slice(0, maxItem), hasMore);
        });
    };
}
export default SelectingInput;
